import React, { useEffect, useRef, useState } from 'react';
import { Box, Link, Typography } from '@mui/material';
import PercentInputField, { clampPctInput } from './PercentInputField';

const CARD_GRID_COLUMNS = 'minmax(0, 1fr) minmax(0, 1fr) minmax(132px, 160px)';

/** Matches `sectionLabel` in WorkPackageProgressModal (Schedule / Earned). */
const budgetSectionLabelSx = {
  fontSize: '10px',
  fontWeight: 600,
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
};

const headerRowSx = {
  display: 'flex',
  alignItems: 'baseline',
  flexWrap: 'wrap',
  gap: 1,
  columnGap: 1.25,
  mb: 0.75,
};

const editLinkSx = {
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  letterSpacing: '0.02em',
  color: 'primary.main',
  opacity: 0.72,
  verticalAlign: 'baseline',
  cursor: 'pointer',
  lineHeight: 1.2,
  '&:hover': { opacity: 1 },
  '&.Mui-disabled': { opacity: 0.38 },
};

const confirmLinkSx = {
  ...editLinkSx,
  opacity: 0.9,
  textDecoration: 'none',
  minWidth: '1.5em',
  fontSize: '16px',
};

const cancelLinkSx = {
  fontSize: '16px',
  fontWeight: 600,
  color: 'text.secondary',
  textTransform: 'none',
  letterSpacing: '0.02em',
  cursor: 'pointer',
  textDecoration: 'none',
  minWidth: '1.5em',
  lineHeight: 1.2,
  '&:hover': { color: 'text.primary' },
};

/**
 * Prefabrication budget split above Shop | Field | Total cards; label-row Edit
 * toggles linked PercentInputFields (same behavior as former Budget allocation block).
 */
const BudgetAllocationHeader = ({
  open = true,
  shopAllocPct,
  onShopAllocPctChange,
  /** When set, confirm awaits this (e.g. PATCH budget) before exiting edit mode. */
  onPersistShopAllocPct,
  canEditBudget,
  disabled,
}) => {
  const [editing, setEditing] = useState(false);
  const [draftShop, setDraftShop] = useState(shopAllocPct);
  const [persisting, setPersisting] = useState(false);
  /** Shop % when Edit was opened; used to restore parent + cards on Cancel. */
  const baselineShopAllocRef = useRef(shopAllocPct);

  useEffect(() => {
    if (!open) setEditing(false);
  }, [open]);

  useEffect(() => {
    if (!editing) setDraftShop(shopAllocPct);
  }, [shopAllocPct, editing]);

  const fieldAllocPct = 100 - shopAllocPct;
  const draftField = 100 - draftShop;

  const startEdit = () => {
    baselineShopAllocRef.current = shopAllocPct;
    setDraftShop(shopAllocPct);
    setEditing(true);
  };

  const cancel = () => {
    onShopAllocPctChange(baselineShopAllocRef.current);
    setDraftShop(baselineShopAllocRef.current);
    setEditing(false);
  };

  const confirm = async () => {
    if (persisting) return;
    const next = clampPctInput(draftShop);
    onShopAllocPctChange(next);
    if (onPersistShopAllocPct) {
      setPersisting(true);
      try {
        await onPersistShopAllocPct(next);
        setEditing(false);
      } catch {
        onShopAllocPctChange(baselineShopAllocRef.current);
        setDraftShop(baselineShopAllocRef.current);
      } finally {
        setPersisting(false);
      }
    } else {
      setEditing(false);
    }
  };

  const onShopDraft = (e) => {
    const raw = e.target.value;
    const next = raw === '' ? 0 : clampPctInput(raw);
    setDraftShop(next);
    onShopAllocPctChange(next);
  };

  const onFieldDraft = (e) => {
    const raw = e.target.value;
    const nextShop = raw === '' ? 100 : 100 - clampPctInput(raw);
    setDraftShop(nextShop);
    onShopAllocPctChange(nextShop);
  };

  const staticPct = (pct) => (
    <Typography
      component="div"
      sx={{
        fontSize: '14px',
        fontWeight: 800,
        letterSpacing: '0.02em',
        color: 'text.primary',
        textAlign: 'center',
      }}
    >
      {pct}%
    </Typography>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Box sx={headerRowSx}>
        <Typography component="span" sx={budgetSectionLabelSx}>
          Prefabrication budget allocation
        </Typography>
        {canEditBudget && !editing && (
          <Link
            component="button"
            type="button"
            onClick={startEdit}
            disabled={disabled || persisting}
            underline="hover"
            sx={editLinkSx}
            aria-label="Edit prefabrication budget allocation"
          >
            Edit
          </Link>
        )}
        {canEditBudget && editing && (
          <>
            <Link
              component="button"
              type="button"
              onClick={() => {
                void confirm();
              }}
              disabled={disabled || persisting}
              underline="none"
              sx={confirmLinkSx}
              aria-label="Confirm budget allocation"
            >
              ✓
            </Link>
            <Link
              component="button"
              type="button"
              onClick={cancel}
              disabled={disabled || persisting}
              underline="none"
              sx={cancelLinkSx}
              aria-label="Cancel budget allocation edit"
            >
              ✗
            </Link>
          </>
        )}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: CARD_GRID_COLUMNS,
          gap: 2,
          alignItems: 'center',
          minHeight: 40,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: 0,
          }}
        >
          {editing ? (
            <PercentInputField
              value={draftShop}
              onChange={onShopDraft}
              disabled={disabled || persisting}
              inputProps={{ 'aria-label': 'Shop budget percent' }}
            />
          ) : (
            staticPct(shopAllocPct)
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: 0,
          }}
        >
          {editing ? (
            <PercentInputField
              value={draftField}
              onChange={onFieldDraft}
              disabled={disabled || persisting}
              inputProps={{ 'aria-label': 'Field budget percent' }}
            />
          ) : (
            staticPct(fieldAllocPct)
          )}
        </Box>

        <Box sx={{ minWidth: 0 }} aria-hidden />
      </Box>
    </Box>
  );
};

export default BudgetAllocationHeader;
